// ─────────────────────────────────────────────────────────────────────────────
// useNodeFilters — client-side search + status filter + column sort.
//   Owns the list-view controls and derives the visible `rows` from the raw
//   node list. `toggleSort` cycles a column between asc → desc, resetting to
//   asc when a different column is picked.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useMemo, useCallback } from "react";

// Columns searched by the free-text box.
const SEARCH_FIELDS = ["node_id", "region", "country", "ip", "node_type"];

export function useNodeFilters(nodes) {
  const [search,       setSearch]       = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortCol,      setSortCol]      = useState(null);
  const [sortDir,      setSortDir]      = useState("asc");

  const rows = useMemo(() => {
    let data = nodes;

    if (filterStatus !== "All") {
      data = data.filter((n) => n.status === filterStatus);
    }

    if (search) {
      const q = search.toLowerCase();
      data = data.filter((n) =>
        SEARCH_FIELDS.some((f) => String(n[f] ?? "").toLowerCase().includes(q))
      );
    }

    if (sortCol) {
      data = [...data].sort((a, b) => {
        let av = a[sortCol] ?? "";
        let bv = b[sortCol] ?? "";
        if (typeof av === "string") av = av.toLowerCase();
        if (typeof bv === "string") bv = bv.toLowerCase();
        return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
      });
    }

    return data;
  }, [nodes, search, filterStatus, sortCol, sortDir]);

  const toggleSort = useCallback((col) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return col;
    });
  }, []);

  return {
    search, setSearch,
    filterStatus, setFilterStatus,
    sortCol, sortDir, toggleSort,
    rows,
    // Lets the table tell "nothing matched your filter" apart from "you have no
    // nodes" — two different messages for two different situations.
    isFiltered: search.trim() !== "" || filterStatus !== "All",
  };
}
